import type { ComponentProps } from 'react';
import type { UseFormRegister } from 'react-hook-form';

import { Flex } from '@repo/ui/Flex';
import { Text } from '../../Language/Language';
import { RadioButton } from '../../RadioButton/RadioButton';
import { SkeletonLoader } from '../../SkeletonLoader/SkeletonLoader';
import { TaskHeader } from '../TaskPreview/TaskPreview';
import { ExchangePreview } from './ExchangePreview';
import type { WorkflowData } from '../../../workflow';
import type { ResolutionOption } from '../../../graphql/query/getResolutionOptions';

import s from './ResolutionTask.module.css';

const Loader = () => (
  <SkeletonLoader
    padding={['py-24', 'px-16']}
    itemHeight={30}
    layout={{
      repeat: 2,
      items: {
        default: [
          {
            columns: 1,
            rows: 1,
          },
          {
            columns: 7,
            rows: 1,
            startColumn: 2,
            startRow: 1,
          },
          {
            columns: 5,
            rows: 1,
            startColumn: 2,
            startRow: 2,
          },
        ],
        mobile: [
          {
            columns: 2,
            rows: 1,
          },
          {
            columns: 6,
            rows: 1,
            startColumn: 3,
            startRow: 1,
          },
          {
            columns: 4,
            rows: 1,
            startColumn: 3,
            startRow: 2,
          },
        ],
      },
    }}
  />
);

export interface ResolutionTaskViewProps {
  itemId?: string;
  isLoading: boolean;
  resolutionOptions?: ResolutionOption[];
  exchangeItem?: {
    productId: string;
    variantId: string;
  };
  // Callbacks
  onChange?: ComponentProps<'input'>['onChange'];
  onResolutionClick?: (option: ResolutionOption) => void;
  // Form integration
  register?: UseFormRegister<WorkflowData>;
}

/**
 * Pure presentational component for displaying resolution options.
 * Contains no hooks or business logic.
 * All data and callbacks provided via props.
 */
export const ResolutionTaskView = ({
  itemId,
  isLoading,
  resolutionOptions,
  exchangeItem,
  onChange,
  onResolutionClick,
  register,
}: ResolutionTaskViewProps) => {
  if (
    !itemId ||
    (!isLoading && (!resolutionOptions || resolutionOptions.length < 1))
  ) {
    return null;
  }

  return (
    <>
      <TaskHeader
        language={{
          langKey: 'Global.resolutions.resolutionSelection.Header',
        }}
        id="resolutionsTitle"
      />
      {isLoading ? (
        <Loader />
      ) : (
        <Flex role="radiogroup" direction="column">
          {resolutionOptions?.map((option) => {
            const registerProps =
              register && itemId
                ? register(`items.${itemId}.resolution.resolutionId`, {
                    onChange,
                  })
                : { name: 'resolution.resolutionId' as const };

            return (
              <RadioButton
                key={option.id}
                value={option.id}
                id={option.id}
                className={s.radio}
                appendedChildren={
                  option.exchanges ? (
                    <ExchangePreview
                      itemId={itemId}
                      resolutionData={option}
                      exchangeItem={exchangeItem}
                    />
                  ) : null
                }
                onClick={() => onResolutionClick?.(option)}
                {...registerProps}
              >
                <Flex direction="column" gap="g-8">
                  <div className={s.resolutionTitle}>
                    <Text
                      langKey={`Global.resolutions.resolutionSelection.${option.id}`}
                    />
                  </div>
                  <div className={s.resolutionDescription}>
                    <Text
                      langKey={`Global.resolutions.resolutionSelection.${option.id}.description`}
                    />
                  </div>
                </Flex>
              </RadioButton>
            );
          })}
        </Flex>
      )}
    </>
  );
};
